import type { Account, Session } from '../types'

const SESSION_KEY = 'investment-course.session.v1'
const DEVICE_ID_KEY = 'investment-course.device-id.v1'

export function readSession(): Session | null {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(SESSION_KEY) ?? 'null') as Partial<Session> | null
    if (!parsed?.account?.accountId || !parsed.account.email || !parsed.token || !parsed.expiresAt) return null
    const expiresAt = Date.parse(parsed.expiresAt)
    if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
      clearSession()
      return null
    }
    return parsed as Session
  } catch {
    return null
  }
}

export function storeSession(session: Session): void {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(session))
  } catch {
    throw new Error('This browser cannot keep the sign-in session.')
  }
}

export function refreshSessionExpiry(token: string, expiresAt: string): boolean {
  const expiry = Date.parse(expiresAt)
  if (!token || !Number.isFinite(expiry) || expiry <= Date.now()) return false
  const session = readSession()
  if (!session || session.token !== token) return false
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({ ...session, expiresAt }))
    return true
  } catch {
    return false
  }
}

export function clearSession(): void {
  try {
    sessionStorage.removeItem(SESSION_KEY)
  } catch {
    // Blocked storage is equivalent to a signed-out browser.
  }
}

export function getDevice(): { deviceId: string; deviceName: string } {
  let deviceId = ''
  try {
    deviceId = localStorage.getItem(DEVICE_ID_KEY) ?? ''
  } catch {
    // Continue with a visit-only device identifier.
  }
  if (!deviceId) {
    deviceId = crypto.randomUUID()
    try {
      localStorage.setItem(DEVICE_ID_KEY, deviceId)
    } catch {
      // The generated ID remains valid for this visit.
    }
  }
  const deviceName = `${navigator.platform || 'Browser'} · ${navigator.userAgent.split(' ').slice(-2).join(' ')}`.slice(0, 80)
  return { deviceId, deviceName }
}

export function accountFrom(data: unknown): Account | null {
  if (!data || typeof data !== 'object') return null
  const value = data as { accountId?: unknown; email?: unknown; createdAt?: unknown }
  if (typeof value.accountId !== 'string' || typeof value.email !== 'string') return null
  return {
    accountId: value.accountId,
    email: value.email,
    ...(typeof value.createdAt === 'string' ? { createdAt: value.createdAt } : {}),
  }
}
